<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'FieldsetContractInvoicing',
    inheritAttrs: false,
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    contract: {
      type: Object,
      required: true,
    },
    labelAlign: {
      type: String,
      required: false,
      default: 'right',
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const updateBillingCycle = (...args) => {
    props.contract.invoicing.billing_cycle = `${billing_frequency.value.value} ${billing_frequency.value.unit}`;
  };

  const currencies = ref(null);
  const billing_frequency = ref(null);
  const billingFrequencyOptions = ref([
    {
      value: 'day',
      text: $fn.value.gettext('day'),
    },
    {
      value: 'month',
      text: $fn.value.gettext('month'),
    },
  ]);
  const contractPaymentTermTypes = ref(null);
  const ux = ref([]);

  const $data = computed(() => ({
    currencies: currencies.value,
    billing_frequency: billing_frequency.value,
    billingFrequencyOptions: billingFrequencyOptions.value,
    contractPaymentTermTypes: contractPaymentTermTypes.value,
    ux: ux.value,
  }));

  const $$load_currencies = async () => {
    return $fn.value.fetch('list_currency');
  };
  const $$load_billing_frequency = async () => {
    return props.contract.invoicing.billing_cycle != null
      ? {
          value: props.contract.invoicing.billing_cycle.split(' ')[0],
          unit: props.contract.invoicing.billing_cycle.split(' ')[1],
        }
      : {
          value: null,
          unit: null,
        };
  };
  const $$load_contractPaymentTermTypes = async () => {
    return $fn.value.getEnumValuesFromSchema('ContractPaymentTermTypes');
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'currencies' },
      { name: 'billing_frequency' },
      { name: 'billingFrequencyOptions' },
      { name: 'contractPaymentTermTypes' },
      { name: 'ux' },
    ],
    loaders: {
      currencies: $$load_currencies,
      billing_frequency: $$load_billing_frequency,
      contractPaymentTermTypes: $$load_contractPaymentTermTypes,
    },
    ux: {},
    dataRefs: {
      currencies,
      billing_frequency,
      billingFrequencyOptions,
      contractPaymentTermTypes,
      ux,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.contract,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('contract');
    }
  );
  watch(
    () => props.labelAlign,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('labelAlign');
    }
  );

  const act_1485536987289644_script_0epqvz51z_value_callable = async (
    $event
  ) => {
    return updateBillingCycle();
  };
  const act_1485536987289644_script_0epqvz51z_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_1485536987289644_script_0epqvz51z_value_callable($event);
      },
    };
  };
  const act_8376657076005968_script_e8ne7z5ax_value_callable = async (
    $event
  ) => {
    return updateBillingCycle();
  };
  const act_8376657076005968_script_e8ne7z5ax_getActionArgs = async (
    $event
  ) => {
    return {
      value: async () => {
        await act_8376657076005968_script_e8ne7z5ax_value_callable($event);
      },
    };
  };

  const act_1485536987289644_script_0epqvz51z = new actionTypes.ScriptAction({
    actionArgs: act_1485536987289644_script_0epqvz51z_getActionArgs,
  });
  const inst_evh_1485536987289644_input_hb9b3m89y =
    new actionTypes.EventHandlerImpl({
      action: act_1485536987289644_script_0epqvz51z,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_1485536987289644_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1485536987289644_input_hb9b3m89y],
    event: 'input',
  });
  const act_8376657076005968_script_e8ne7z5ax = new actionTypes.ScriptAction({
    actionArgs: act_8376657076005968_script_e8ne7z5ax_getActionArgs,
  });
  const inst_evh_8376657076005968_input_754ep9rk0 =
    new actionTypes.EventHandlerImpl({
      action: act_8376657076005968_script_e8ne7z5ax,
      event: 'input',
      displayName: 'script',
    });
  const inst_evh_8376657076005968_input = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8376657076005968_input_754ep9rk0],
    event: 'input',
  });

  const actions = {
    inst_evh_1485536987289644_input_hb9b3m89y,
    inst_evh_1485536987289644_input,
    inst_evh_8376657076005968_input_754ep9rk0,
    inst_evh_8376657076005968_input,
  };

  const evh_1485536987289644_input_hb9b3m89y = async (event, scope) => {
    return await actions.inst_evh_1485536987289644_input_hb9b3m89y.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1485536987289644_input = async (event, scope) => {
    return await actions.inst_evh_1485536987289644_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8376657076005968_input_754ep9rk0 = async (event, scope) => {
    return await actions.inst_evh_8376657076005968_input_754ep9rk0.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8376657076005968_input = async (event, scope) => {
    return await actions.inst_evh_8376657076005968_input.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpFormGroup
    name="rental_fee_advances_initial_months"
    :label="pgettext('Contract', 'Initial advances')"
    :appendText="pgettext('InputAddon', 'months')"
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_initial_months"
      v-model="contract.invoicing.rental_fee_advances_initial_months"
      :value="contract.invoicing.rental_fee_advances_initial_months"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.rental_fee_advances_initial_months =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormInput>
  </ItpFormGroup>
  <ItpFormGroup
    name="rental_fee_advances_reserve_months"
    :label="pgettext('Contract', 'Reserve advances')"
    :appendText="pgettext('InputAddon', 'months')"
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
  >
    <ItpFormInput
      type="number"
      name="rental_fee_advances_reserve_months"
      v-model="contract.invoicing.rental_fee_advances_reserve_months"
      :value="contract.invoicing.rental_fee_advances_reserve_months"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.rental_fee_advances_reserve_months =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormInput>
  </ItpFormGroup>
  <hr />
  <ItpFormGroup
    name="billing_currency"
    :label="pgettext('Contract', 'Billing currency')"
    :label-cols-md="4"
    :content-cols-md="3"
    :labelAlign="labelAlign"
  >
    <ItpFormSelect2
      name="billing_currency"
      valueField="code"
      textField="code"
      required
      v-model="contract.invoicing.billing_currency"
      :value="contract.invoicing.billing_currency"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.billing_currency =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
      :options="currencies"
    >
    </ItpFormSelect2>
  </ItpFormGroup>
  <ItpFormGroup
    name="billing_frequency"
    :label="pgettext('Contract', 'Billing frequency')"
    :label-cols-md="4"
    :content-cols-md="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="loaded"
  >
    <ItpFormInput
      type="number"
      name="billing_frequency_value"
      v-model="billing_frequency.value"
      :value="billing_frequency.value"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
          (billing_frequency.value =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event);
        evh_1485536987289644_input($event, {});
      "
    >
    </ItpFormInput>
    <ItpFormSelect2
      name="billing_frequency_unit"
      v-model="billing_frequency.unit"
      :value="billing_frequency.unit"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
          (billing_frequency.unit =
            $event && typeof $event === 'object' && 'data' in $event
              ? $event.data
              : $event);
        evh_8376657076005968_input($event, {});
      "
      :options="billingFrequencyOptions"
      class="w-50"
    >
    </ItpFormSelect2>
  </ItpFormGroup>
  <ItpFormGroup
    name="payment_terms"
    :label="pgettext('Contract', 'Payment period')"
    :label-cols-md="4"
    :content-cols-md="6"
    :labelAlign="labelAlign"
    contentClass="input-group"
    v-if="contract.invoicing.payment_terms"
  >
    <ItpFormInput
      type="number"
      name="payment_terms_value"
      v-model="contract.invoicing.payment_terms.value"
      :value="contract.invoicing.payment_terms.value"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.payment_terms.value =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormInput>
    <ItpFormSelect2
      name="payment_terms_type"
      v-model="contract.invoicing.payment_terms.type"
      :value="contract.invoicing.payment_terms.type"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.payment_terms.type =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
      :options="contractPaymentTermTypes"
      class="w-50"
    >
    </ItpFormSelect2>
  </ItpFormGroup>
  <hr />
  <ItpFormGroup
    name="invoice_notes"
    :label="pgettext('Contract', 'Notes on invoice')"
    :label-cols-md="4"
    :content-cols-md="8"
    :labelAlign="labelAlign"
  >
    <ItpFormTextarea
      name="invoice_notes_value"
      v-model="contract.invoicing.invoice_notes"
      :value="contract.invoicing.invoice_notes"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.invoice_notes =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormTextarea>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_contract_number"
    :label="pgettext('Contract', 'Contract in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the contract number in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_contract_number"
      v-model="contract.invoicing.invoice_notes_config.include_contract_number"
      :value="contract.invoicing.invoice_notes_config.include_contract_number"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.invoice_notes_config.include_contract_number =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_rental"
    :label="pgettext('Contract', 'Rental in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the rental name in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_rental"
      v-model="contract.invoicing.invoice_notes_config.include_rental"
      :value="contract.invoicing.invoice_notes_config.include_rental"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.invoice_notes_config.include_rental =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
  <ItpFormGroup
    name="include_invoice_period"
    :label="pgettext('Contract', 'Period in invoice notes')"
    :helpTooltip="
      pgettext(
        'Contract',
        'Whether to include the billing period in the invoice notes when generating invoices.'
      )
    "
    :label-cols-md="4"
    :content-cols-md="4"
    :labelAlign="labelAlign"
    v-if="contract.invoicing.invoice_notes_config"
  >
    <ItpFormCheckbox
      name="include_invoice_period"
      v-model="contract.invoicing.invoice_notes_config.include_invoice_period"
      :value="contract.invoicing.invoice_notes_config.include_invoice_period"
      @input="
        !($event && typeof $event.stopPropagation === 'function') &&
        (contract.invoicing.invoice_notes_config.include_invoice_period =
          $event && typeof $event === 'object' && 'data' in $event
            ? $event.data
            : $event)
      "
    >
    </ItpFormCheckbox>
  </ItpFormGroup>
</template>
